import {
  ActionReducerMapBuilder,
  createReducer,
  PayloadAction,
} from "@reduxjs/toolkit";
import { DefaultNaerasState, NaerasState } from "../state/stateItems";
import {
  TNaerasBid,
  TNaerasBidsResponse,
  TNaerasCalculateResponse,
  TNaerasCalendar,
  TNaerasDashboard,
  TNaerasFinalizeResponse,
  TNaerasImportResponse,
  TNaerasJobRun,
  TNaerasJobRunsResponse,
  TNaerasPoolState,
  TNaerasSettings,
} from "@/data/types";
import {
  adjustNaerasBidAsync,
  calculateNaerasBidsAsync,
  getNaerasBidAsync,
  getNaerasBidsAsync,
  finalizeNaerasBidsAsync,
  getNaerasCalendarAsync,
  getNaerasDashboardAsync,
  getNaerasJobRunsAsync,
  getNaerasPoolAsync,
  getNaerasSettingsAsync,
  getNaerasTodayJobRunsAsync,
  importNaerasBidsAsync,
  updateNaerasSettingsAsync,
} from "../actions";

/** One row per target_date, so every screen reads the same bid. */
const upsert = (state: NaerasState, bids: TNaerasBid[]) => {
  bids.forEach((bid) => {
    state.bids[bid.target_date] = bid;
  });
};

const handleGetCalendar = (
  state: NaerasState,
  action: PayloadAction<TNaerasCalendar>
) => {
  if (!action?.payload) {
    return state;
  }

  state.calendar = action.payload;
};

const handleGetBids = (
  state: NaerasState,
  action: PayloadAction<TNaerasBidsResponse>
) => {
  if (!action?.payload) {
    return state;
  }

  const { items } = action.payload;

  state.items = items;
  upsert(state, items);
};

const handleGetBid = (state: NaerasState, action: PayloadAction<TNaerasBid>) => {
  if (!action?.payload) {
    return state;
  }

  upsert(state, [action.payload]);
};

const handleCalculateBids = (
  state: NaerasState,
  action: PayloadAction<TNaerasCalculateResponse>
) => {
  if (!action?.payload) {
    return state;
  }

  upsert(state, action.payload.items);
};

const handleImportBids = (
  state: NaerasState,
  action: PayloadAction<TNaerasImportResponse>
) => {
  if (!action?.payload) {
    return state;
  }

  upsert(state, action.payload.items);
};

/**
 * Finalization returns the days it locked. The bid that absorbed a revision's
 * correction is *not* among them — only its date and delta are reported — so it
 * stays untouched here and the page refetches it.
 */
const handleFinalizeBids = (
  state: NaerasState,
  action: PayloadAction<TNaerasFinalizeResponse>
) => {
  if (!action?.payload) {
    return state;
  }

  upsert(state, action.payload.items);
};

/**
 * Finalizing a bid re-runs Job B on the spot, and the API reports that run as
 * `B_FINALIZE`. It is the same work with the same result shape, only not on
 * the schedule — so it reads as Job B, flagged as manual.
 */
const normaliseJobRun = (run: TNaerasJobRun): TNaerasJobRun =>
  (run.job as string) === "B_FINALIZE" ? { ...run, job: "B", manual: true } : run;

/**
 * Job runs page by cursor, not by page number: a request that carried one is
 * asking for the next slice, so its rows are appended rather than replacing
 * what is already on screen.
 */
const handleGetJobRuns = (
  state: NaerasState,
  action: PayloadAction<
    TNaerasJobRunsResponse,
    string,
    { arg?: { params?: { cursor?: string } } }
  >
) => {
  if (!action?.payload) {
    return state;
  }

  const items = action.payload.items.map(normaliseJobRun);
  const { next_cursor } = action.payload;

  state.jobRuns = action.meta?.arg?.params?.cursor ? [...state.jobRuns, ...items] : items;
  state.jobRunsCursor = next_cursor;
};

const handleGetTodayJobRuns = (
  state: NaerasState,
  action: PayloadAction<TNaerasJobRunsResponse>
) => {
  if (!action?.payload) {
    return state;
  }

  state.todayRuns = action.payload.items.map(normaliseJobRun);
};

const handleGetPool = (
  state: NaerasState,
  action: PayloadAction<
    TNaerasPoolState,
    string,
    { arg?: { params?: { date?: string } } }
  >
) => {
  // A lookup for another day (the edit dialog) must not replace today's pool
  if (!action?.payload || action.meta?.arg?.params?.date) {
    return state;
  }

  state.pool = action.payload;
};

const handleGetDashboard = (
  state: NaerasState,
  action: PayloadAction<TNaerasDashboard>
) => {
  if (!action?.payload) {
    return state;
  }

  state.dashboard = action.payload;
};

const handleGetSettings = (
  state: NaerasState,
  action: PayloadAction<TNaerasSettings>
) => {
  if (!action?.payload) {
    return state;
  }

  state.settings = action.payload;
};

const reducerBuilder = (builder: ActionReducerMapBuilder<NaerasState>) => {
  builder.addCase(getNaerasCalendarAsync.fulfilled.type, handleGetCalendar);
  builder.addCase(getNaerasBidsAsync.fulfilled.type, handleGetBids);
  builder.addCase(getNaerasBidAsync.fulfilled.type, handleGetBid);
  builder.addCase(calculateNaerasBidsAsync.fulfilled.type, handleCalculateBids);
  // The PATCH response carries recalculated limits, so it replaces the row.
  builder.addCase(adjustNaerasBidAsync.fulfilled.type, handleGetBid);
  builder.addCase(importNaerasBidsAsync.fulfilled.type, handleImportBids);
  builder.addCase(finalizeNaerasBidsAsync.fulfilled.type, handleFinalizeBids);
  builder.addCase(getNaerasJobRunsAsync.fulfilled.type, handleGetJobRuns);
  builder.addCase(getNaerasTodayJobRunsAsync.fulfilled.type, handleGetTodayJobRuns);
  builder.addCase(getNaerasPoolAsync.fulfilled.type, handleGetPool);
  builder.addCase(getNaerasDashboardAsync.fulfilled.type, handleGetDashboard);
  // The PATCH answers with the same body as the GET.
  builder.addCase(getNaerasSettingsAsync.fulfilled.type, handleGetSettings);
  builder.addCase(updateNaerasSettingsAsync.fulfilled.type, handleGetSettings);
};

export default createReducer(DefaultNaerasState, reducerBuilder);
